import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Printer, MessageSquare, Loader2, Send } from 'lucide-react';

/** Opens a small print-friendly window with the counseling points and triggers the browser print dialog. */
function _escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/** Opens a small print-friendly window with the counseling points and triggers the browser print dialog.
 * Returns true if the print window actually opened, false if the pop-up was blocked — callers use this
 * to decide whether a print actually happened (and is therefore worth logging for audit). */
export function printCounselingSlip(alerts) {
  if (!alerts?.length) return false;
  const rows = alerts.map((a) => `
    <tr>
      <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-weight:600;white-space:nowrap;">${_escapeHtml((a.drug_pair || []).join(' + '))}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">${_escapeHtml(a.description)}</td>
    </tr>`).join('');
  const html = `<!DOCTYPE html><html><head><title>Counseling Slip</title>
    <meta charset="utf-8" />
    <style>
      body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#1f2937;}
      h1{font-size:18px;margin:0 0 4px;}
      p.sub{font-size:12px;color:#6b7280;margin:0 0 16px;}
      table{width:100%;border-collapse:collapse;font-size:13px;}
      th{text-align:left;padding:8px 10px;border-bottom:2px solid #1f2937;font-size:11px;text-transform:uppercase;letter-spacing:.03em;}
    </style></head><body>
    <h1>Pharmacy Counseling Notice</h1>
    <p class="sub">${new Date().toLocaleString()}</p>
    <table><thead><tr><th>Items</th><th>Counseling point</th></tr></thead><tbody>${rows}</tbody></table>
    <p class="sub" style="margin-top:20px;">Please share this with the patient before dispensing.</p>
    </body></html>`;
  const win = window.open('', '_blank', 'width=480,height=640');
  if (!win) {
    toast.error('Please allow pop-ups to print the counseling slip');
    return false;
  }
  win.document.write(html);
  win.document.close();
  setTimeout(() => { try { win.focus(); win.print(); } catch { /* window was closed by the user */ } }, 250);
  return true;
}

/** Print / SMS actions for a list of food_alerts (POS cart or checkout). */
export const CounselingSlipActions = ({ alerts, patientId, cartNames, defaultPhone, testIdPrefix }) => {
  const [showSms, setShowSms] = useState(false);
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);

  if (!alerts?.length) return null;

  // Best-effort audit log for the patient's record — never blocks or
  // interrupts the print dialog if it fails. The server re-derives the
  // counseling points itself, so only patient_id/cart_names are sent.
  const logPrint = () => {
    api.post('/pos/food-alerts/print-log', {
      patient_id: patientId || null, cart_names: cartNames || [],
    }).catch(() => { /* audit log only — printing has already happened */ });
  };

  // defaultPhone (customer/patient phone) can change AFTER this component has
  // already mounted — e.g. a patient is picked after the checkout dialog
  // opens — so seed the field from it at the moment SMS is opened, not at mount.
  const toggleSms = () => {
    setShowSms((wasOpen) => {
      const willOpen = !wasOpen;
      if (willOpen && !phone) setPhone(defaultPhone || '');
      return willOpen;
    });
  };

  const sendSms = async () => {
    if (!phone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setSending(true);
    try {
      await api.post('/pos/food-alerts/sms', { patient_id: patientId || null, cart_names: cartNames || [], phone: phone.trim() });
      toast.success('Counseling slip sent by SMS');
      setShowSms(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the counseling slip by SMS');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
      <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] gap-1"
        onClick={() => { if (printCounselingSlip(alerts)) logPrint(); }} data-testid={`${testIdPrefix}-print`}>
        <Printer className="h-3 w-3" />Print
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] gap-1"
        onClick={toggleSms} data-testid={`${testIdPrefix}-sms-toggle`}>
        <MessageSquare className="h-3 w-3" />SMS
      </Button>
      {showSms && (
        <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-sms-form`}>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0244..."
            className="h-6 w-28 text-[10px] px-1.5" data-testid={`${testIdPrefix}-sms-phone`} />
          <Button type="button" size="sm" className="h-6 px-1.5" disabled={sending} onClick={sendSms}
            data-testid={`${testIdPrefix}-sms-send`}>
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      )}
    </div>
  );
};
